// feature/camilo-action-panel, simplified Sprint 54b.
// feature/response-docs-retire — response-upload blocks removed; team handles
// court responses via external email outside Athena.
//
// Action blocks remaining:
//   Block A  ¿Recibiste respuesta? (Marcar insumos recibidos)
//            insumos_solicitados / expediente_solicitado
//   Block B  Reparto a abogado (Repartir igualmente / override)
//            pendiente_reparto_lawyer / insumos_recibidos (normal)
//            insumos_solicitados / expediente_solicitado (override path)
//
// All blocks self-gate on case estado; not all visible at once.

import React, { useEffect, useRef, useState } from "react";
// feature/draft-persistence. Persist text fields to
// localStorage so a crash, reload, or browser-translate-induced React
// recovery doesn't blow Camilo's typed work.
import useDraftField from "../../hooks/useDraftField";
import CenteredModal from "../../components/CenteredModal";
import Spinner from "../../components/Spinner";
import { TipoPill, EstadoPill, ContestadaTag } from "../../components/Pills";
import { cases as casesApi, insumos as insumosApi, users as usersApi } from "../../api/endpoints";
import { useToast } from "../../components/Toast";

const MARK_RECEIVED_STATES = new Set(["insumos_solicitados", "expediente_solicitado"]);
const REPARTO_NORMAL_STATES = new Set(["pendiente_reparto_lawyer", "insumos_recibidos"]);
const REPARTO_OVERRIDE_STATES = new Set(["insumos_solicitados", "expediente_solicitado"]);
const TERMINAL_NO_ACTIONS = new Set(["cerrado", "devuelto", "devuelto_a_reparto", "error_marcado"]);

export default function CamiloCaseActionPanel({ caseRow, open, onClose, onCaseUpdate }) {
  const toast = useToast();
  const [data, setData] = useState(null);

  // Stale-fetch guard, same pattern as CaseDetail (Sprint 50). When Camilo
  // closes one row's panel and immediately opens another, the older fetch
  // shouldn't overwrite the newer caseRow's state.
  const fetchVersionRef = useRef(0);

  useEffect(() => {
    if (!open || !caseRow?.id) return;
    setData(null);
    const myVersion = ++fetchVersionRef.current;

    casesApi.get(caseRow.id)
      .then((detailRes) => {
        if (myVersion !== fetchVersionRef.current) return;
        if (detailRes?.data && String(detailRes.data.id) !== String(caseRow.id)) return;
        setData(detailRes.data);
      })
      .catch(() => {
        if (myVersion !== fetchVersionRef.current) return;
        toast.push("No se pudo cargar el caso", "error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, caseRow?.id]);

  function refresh() {
    if (!caseRow?.id) return;
    const myVersion = ++fetchVersionRef.current;
    casesApi.get(caseRow.id).then((detailRes) => {
      if (myVersion !== fetchVersionRef.current) return;
      setData(detailRes.data);
    });
    onCaseUpdate?.();
  }

  const c = data || caseRow;
  // Sprint 56 — surface a banner to Camilo when the lawyer is currently
  // Esperando insumo on this case, so he knows uploading the response
  // will start the lawyer's 3h Timer 2 (rather than just routing).
  const lawyerEsperando = c && c.esperando_insumo_at && !c.esperando_insumo_released_at;

  return (
    <>
      <CenteredModal
        open={open}
        onClose={onClose}
        title={c ? c.case_number : "Cargando…"}
        subtitle={c ? `${c.accionante_nombre || c.accionante || "—"} · ${c.juzgado || "—"}` : undefined}
        headerTags={c ? (
          <>
            <TipoPill tipo={c.tipo} />
            <EstadoPill c={c} />
            <ContestadaTag contestadaAt={c.contestada_at} estado={c.estado} dateFormat="short" />
            {lawyerEsperando && (
              <span className="badge warning" style={{ fontSize: 10 }}>⏰ Abogado esperando insumo</span>
            )}
          </>
        ) : null}
        size="lg"
      >
        {c ? (
          <div className="col" style={{ gap: 20 }}>
            {lawyerEsperando && (
              <div className="card tight" style={{
                padding: 12, borderLeft: "3px solid var(--color-warning)",
                background: "var(--color-warning-bg)",
              }}>
                <strong style={{ color: "var(--color-warning-ink)" }}>⏰ El abogado está esperando este insumo.</strong>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Cuando cargue la respuesta de Previsora abajo, su plazo de
                  <strong> 3 horas</strong> arranca automáticamente y el caso
                  vuelve a estar bajo su control (no pasa por la cola de "Listos").
                </div>
              </div>
            )}
            <CasoDetailsSection c={c} />
            {MARK_RECEIVED_STATES.has(c.estado) && (
              <ReceivedBlock c={c} onUpdate={refresh} />
            )}
            {(REPARTO_NORMAL_STATES.has(c.estado) || REPARTO_OVERRIDE_STATES.has(c.estado)) && (
              <RepartoBlock c={c} onUpdate={refresh} />
            )}
            {TERMINAL_NO_ACTIONS.has(c.estado) && (
              <ReadOnlyNote estado={c.estado} contestadaAt={c.contestada_at} />
            )}
            {c.estado === "asignado_abogado" && !lawyerEsperando && (
              <AsignadoNote abogadoNombre={c.abogado_nombre} />
            )}
          </div>
        ) : (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        )}
      </CenteredModal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Caso details
// ---------------------------------------------------------------------------
function CasoDetailsSection({ c }) {
  return (
    <section>
      <SectionTitle>📋 Caso</SectionTitle>
      <dl className="kvs">
        <dt>Accionante</dt>
        <dd>{c.accionante_nombre || c.accionante || "—"}</dd>
        <dt>Radicado</dt>
        <dd style={{ fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: 12 }}>
          {c.radicado || "—"}
        </dd>
        <dt>Juzgado</dt>
        <dd>{c.juzgado || "—"}</dd>
        <dt>Equipo insumos</dt>
        <dd>{c.insumos_nombre || c.insumos_user_name || c.insumos_user_email || "—"}</dd>
        {c.abogado_nombre && (
          <>
            <dt>Abogado</dt>
            <dd>{c.abogado_nombre}</dd>
          </>
        )}
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Block A — ¿Recibiste respuesta de Previsora? (Marcar insumos recibidos)
// ---------------------------------------------------------------------------
function ReceivedBlock({ c, onUpdate }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function handleMarkReceived() {
    setBusy(true);
    try {
      await insumosApi.markReceived(c.id);
      toast.push("Insumos marcados como recibidos. El caso pasó a su cola de \"Listos para reparto\".");
      onUpdate?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al marcar como recibidos", "error");
    } finally { setBusy(false); }
  }

  return (
    <section>
      <ActionCard primary>
        <h4>¿Recibió respuesta de Previsora?</h4>
        <p className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
          Cuando llegue la respuesta con los insumos solicitados, marque aquí.
          El caso pasa a su cola de "Listos para reparto" para enviarlo al
          abogado correspondiente. Si va a subir el PDF, hágalo en el bloque
          de abajo — la transición se hace automáticamente.
        </p>
        <button onClick={handleMarkReceived} disabled={busy}>
          {busy && <Spinner color="currentColor" />} Marcar insumos recibidos
        </button>
      </ActionCard>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Block B — Reparto a abogado (Repartir igualmente or override)
// ---------------------------------------------------------------------------
function RepartoBlock({ c, onUpdate }) {
  const toast = useToast();
  const isOverride = REPARTO_OVERRIDE_STATES.has(c.estado);

  // Normal path — has lawyer + ready to route
  // Override path — pre-response state; reason ≥10 chars required

  const [lawyers, setLawyers] = useState([]);
  const [selectedLawyerId, setSelectedLawyerId] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  // feature/camilo-reassign-anywhere.
  // When an abogado is already assigned, expose a "Cambiar abogado"
  // affordance inside the Reparto card so Camilo can move a case to a
  // different lawyer without leaving the dispatcher (previously he had to
  // open the full case-detail modal). Same backend path: cases.reassign.
  const [reassignOpen, setReassignOpen] = useState(false);

  // Sprint 62b — drafts persisted per case so a crash mid-typing
  // doesn't blow Camilo's reasoning. selectedLawyerId stays plain
  // useState (re-pickable from a dropdown, not typed).
  const [fechaEnvio, setFechaEnvio, clearFecha]   = useDraftField(`camilo-reparto-fecha-${c.id}`,  "");
  const [notes,      setNotes,      clearNotes]   = useDraftField(`camilo-reparto-notes-${c.id}`,  "");
  const [reason,     setReason,     clearReason]  = useDraftField(`camilo-reparto-reason-${c.id}`, "");
  const [busy, setBusy] = useState(false);

  // Lazy-load lawyer list when needed.
  // - Assign-first path (no abogado yet): filter by case_tipo so Camilo
  //   gets a sane specialization-matched default.
  // - Reassign path (Sprint 62c, abogado already set): unfiltered so
  //   Camilo can move the case to ANY active abogado regardless of
  //   specialization. The select still shows each lawyer's tags in
  //   the option label so he can pick informedly.
  const needsLawyerList = !c.abogado_asignado_id || reassignOpen;
  useEffect(() => {
    if (!needsLawyerList) return;
    const params = reassignOpen
      ? { role: "abogado" }
      : { role: "abogado", case_tipo: c.tipo };
    usersApi.list(params)
      .then((r) => setLawyers((r.data || []).filter((u) => u.active)))
      .catch(() => toast.push("No se pudo cargar abogados", "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.id, needsLawyerList, reassignOpen]);

  async function handleAssignFirst() {
    if (!selectedLawyerId) return;
    setAssignBusy(true);
    try {
      await casesApi.reassign(c.id, Number(selectedLawyerId));
      toast.push("Abogado asignado.");
      setSelectedLawyerId("");
      onUpdate?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al asignar abogado", "error");
    } finally { setAssignBusy(false); }
  }

  async function handleReassign() {
    // Sprint 62c — reassign in-place when an abogado is already set.
    // Same endpoint as the assign-first path; Camilo can pick a different
    // lawyer (e.g. specialization mismatch, vacation, load balancing).
    if (!selectedLawyerId || Number(selectedLawyerId) === c.abogado_asignado_id) return;
    setAssignBusy(true);
    try {
      await casesApi.reassign(c.id, Number(selectedLawyerId));
      toast.push("Caso reasignado al nuevo abogado.");
      setSelectedLawyerId("");
      setReassignOpen(false);
      onUpdate?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al reasignar", "error");
    } finally { setAssignBusy(false); }
  }

  async function handleRoute() {
    if (isOverride && reason.trim().length < 10) {
      toast.push("La razón debe tener al menos 10 caracteres.", "error");
      return;
    }
    setBusy(true);
    try {
      await insumosApi.routeToLawyer(c.id, {
        notes:       notes.trim() || null,
        reason:      isOverride ? reason.trim() : null,
        fecha_envio: fechaEnvio || null,
      });
      toast.push(isOverride
        ? "Caso repartido en override (sin respuesta de Previsora)."
        : "Caso enviado al abogado responsable.");
      // Sprint 62b — clear persisted drafts on success.
      clearNotes(); clearReason(); clearFecha();
      onUpdate?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al repartir", "error");
    } finally { setBusy(false); }
  }

  // No lawyer assigned — show the assign-first card before the route card.
  if (!c.abogado_asignado_id) {
    return (
      <section>
        <ActionCard primary>
          <h4>Asignar abogado responsable</h4>
          <p className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
            Filtrado por especialización para tipo "{c.tipo}".
          </p>
          <select value={selectedLawyerId}
                  onChange={(e) => setSelectedLawyerId(e.target.value)}
                  disabled={assignBusy}
                  style={{ width: "100%", marginBottom: 8 }}>
            <option value="">— Elegir abogado —</option>
            {lawyers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
                {u.specializations?.length ? ` · ${u.specializations.join(", ")}` : ""}
              </option>
            ))}
          </select>
          {lawyers.length === 0 && (
            <div className="muted" style={{ fontSize: 11, marginBottom: 8 }}>
              Cargando o sin abogados con la especialización requerida…
            </div>
          )}
          <button onClick={handleAssignFirst} disabled={!selectedLawyerId || assignBusy}>
            {assignBusy && <Spinner color="currentColor" />} Asignar abogado
          </button>
        </ActionCard>
      </section>
    );
  }

  return (
    <section>
      <ActionCard primary={!isOverride}>
        <h4 style={isOverride ? { color: "var(--color-warning-ink)" } : undefined}>
          {isOverride ? "⚠ Reparto a abogado sin esperar respuesta" : "Reparto a abogado"}
        </h4>
        <p className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
          {isOverride
            ? <>Si necesita enviar el caso al abogado <strong>{c.abogado_nombre || "—"}</strong> sin
                esperar la respuesta de Previsora, justifique por qué (mínimo 10 caracteres).</>
            : <>Revise los insumos recibidos. Si están completos, envíe el caso al abogado
                responsable: <strong>{c.abogado_nombre || "—"}</strong>.</>}
        </p>
        {/* feature/camilo-reassign-anywhere. Inline reassign
            picker so Camilo can move the case to any other abogado without
            leaving the dispatcher. Closed by default to keep the card
            scannable; opens to a dropdown + confirm/cancel pair. Same
            endpoint (cases.reassign) the assign-first flow uses. */}
        {!reassignOpen ? (
          <button type="button"
                  className="ghost sm"
                  onClick={() => setReassignOpen(true)}
                  disabled={busy || assignBusy}
                  style={{
                    fontSize: 11, padding: "4px 10px",
                    marginBottom: 10,
                    color: "var(--accent-mint)",
                  }}>
            ↻ Reasignar a otro abogado
          </button>
        ) : (
          <div className="card tight" style={{
            padding: 10, marginBottom: 10,
            background: "var(--bg-subtle)",
          }}>
            <label style={{ fontSize: 12, textTransform: "none", letterSpacing: 0, display: "block" }}>
              Mover este caso a otro abogado
              <select value={selectedLawyerId}
                      onChange={(e) => setSelectedLawyerId(e.target.value)}
                      disabled={assignBusy}
                      style={{ width: "100%", marginTop: 4 }}>
                <option value="">— Elegir nuevo abogado —</option>
                {lawyers
                  .filter((u) => u.id !== c.abogado_asignado_id)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.email}
                      {u.specializations?.length ? ` · ${u.specializations.join(", ")}` : ""}
                    </option>
                  ))}
              </select>
            </label>
            {lawyers.length === 0 && (
              <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                Cargando abogados…
              </div>
            )}
            <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end" }}>
              <button type="button" className="secondary sm"
                      onClick={() => { setReassignOpen(false); setSelectedLawyerId(""); }}
                      disabled={assignBusy}
                      style={{ fontSize: 11, padding: "4px 10px" }}>
                Cancelar
              </button>
              <button type="button" className="sm"
                      onClick={handleReassign}
                      disabled={!selectedLawyerId || assignBusy
                                || Number(selectedLawyerId) === c.abogado_asignado_id}
                      style={{ fontSize: 11, padding: "4px 10px" }}>
                {assignBusy && <Spinner color="currentColor" />} Confirmar reasignación
              </button>
            </div>
          </div>
        )}
        {isOverride && (
          <label style={{ fontSize: 12, textTransform: "none", letterSpacing: 0, display: "block" }}>
            Razón obligatoria
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      rows={3} maxLength={500}
                      placeholder="Ej: Previsora confirmó que no responderá; plazo vence mañana."
                      style={{ width: "100%", marginTop: 4 }} />
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              {reason.trim().length} / 10 caracteres mínimos
            </div>
          </label>
        )}
        <label style={{ fontSize: 12, textTransform: "none", letterSpacing: 0, display: "block", marginTop: 10 }}>
          Fecha de envío del insumo (opcional)
          <input type="date" value={fechaEnvio}
                 onChange={(e) => setFechaEnvio(e.target.value)}
                 max={new Date().toISOString().split("T")[0]}
                 style={{ width: "100%", marginTop: 4 }} />
        </label>
        <label style={{ fontSize: 12, textTransform: "none", letterSpacing: 0, display: "block", marginTop: 10 }}>
          Notas (opcional)
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    rows={2} maxLength={500}
                    style={{ width: "100%", marginTop: 4 }} />
        </label>
        <button onClick={handleRoute}
                disabled={busy || (isOverride && reason.trim().length < 10)}
                style={isOverride ? { background: "var(--color-warning)", color: "var(--on-accent)", borderColor: "var(--color-warning)", marginTop: 10 } : { marginTop: 10 }}>
          {busy && <Spinner color="currentColor" />}
          {isOverride ? "Confirmar reparto sin respuesta" : "Repartir al abogado"}
        </button>
      </ActionCard>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Read-only fallbacks
// ---------------------------------------------------------------------------
function ReadOnlyNote({ estado, contestadaAt }) {
  return (
    <section>
      <div className="card tight muted" style={{ fontSize: 13 }}>
        {contestadaAt
          ? "Caso contestado. Sin acciones pendientes."
          : estado === "cerrado"
            ? "Caso cerrado. Sin acciones pendientes."
            : `Estado actual (${estado}) sin acciones disponibles para su rol.`}
      </div>
    </section>
  );
}

function AsignadoNote({ abogadoNombre }) {
  return (
    <section>
      <div className="card tight" style={{ fontSize: 13 }}>
        Este caso ya está con el abogado responsable
        {abogadoNombre ? <> ({abogadoNombre})</> : ""}. No requiere acción suya por ahora.
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------
function SectionTitle({ children }) {
  return (
    <h3 style={{
      margin: "0 0 12px",
      fontSize: 13, fontWeight: 600,
      textTransform: "uppercase", letterSpacing: "0.4px",
      color: "var(--text-muted)",
      display: "flex", alignItems: "center", gap: 8,
    }}>
      {children}
    </h3>
  );
}

function ActionCard({ children, primary }) {
  const borderColor = primary
    ? "var(--accent-mint)"
    : "var(--color-warning)";
  return (
    <div className="card tight" style={{
      borderLeft: `3px solid ${borderColor}`,
      padding: 14,
    }}>
      {children}
    </div>
  );
}
